import { FC, useEffect, useMemo, useRef, useState } from 'react';
import styles from './entry.module.less';
import SkeletonMapDetail from '@/common/components/Skeleton/SkeletonMapDetail';
import V2Container from '@/common/components/Data/V2Container';
import Top from '../index/components/AreaDetailDrawer/components/Top';
import Main from '../index/components/AreaDetailDrawer/components/Main';
import { useMethods } from '@lhb/hook';
import { spotEvaluationDetailShare, spotEvaluationOverviewShare } from '@/common/api/spotevaluation';
import { urlParams } from '@lhb/func';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { getShareContent } from '@/common/api/common';
import { DrawerDataProps, RangeModeEnum, SpotTypeEnum } from '../index/ts-config';
import { SemirModelCode } from '@/views/youth/pages/map/ts-config';
import { typeEnum } from '@/views/surround/pages/index/ts-config';
import V2WaterMark from '@/common/components/Others/V2WaterMark';

/**
 * 点位测评分享详情页
 * @demo http://10.10.8.21:3000/spotevaluation/detail?shareId=f1wj5ezvo64g
 */
const ShareSpotEvaluation: FC<{}> = () => {
  const { shareId } = urlParams(location.search) || {};
  const [waterText, setWaterText] = useState<string>('');
  const [drawerData, setDrawerData] = useState<DrawerDataProps>({
    open: false,
    lng: '',
    lat: '',
    cityId: null,
    cityName: '',
    poiName: '',
    type: '',
    modelClusterId: null,
    modelConfigId: null,
    /** 查询类型 */
    poiSearchType: 1,
    /** 查询范围：半径或时间 */
    radius: null,
    /** 绘制等时圈时的多边形路径 */
    points: [],
    id: null,
  }); // 模拟弹窗需要的传参，不再对组件内进行其他定制化
  const openTimeRef: any = useRef<any>(0);
  const [detail, setDetail] = useState<any>({}); // 详情信息
  const [overViewData, setOverViewData] = useState<any>({}); // 概览卡片
  const [loading, setLoading] = useState<boolean>(false); // 加载中
  const [isLoaded, setLoaded] = useState(false); // 详情是否加载完成
  const [configSwitch, setConfigSwitch] = useState<any>({});
  const [shareParams, setShareParams] = useState<any>({});
  const [isSemirModel, setIsSemirModel] = useState<boolean>(false); // 是否是森马模型

  useEffect(() => {
    if (drawerData.lng && drawerData.lat) {
      // methods.eventTracking();
      methods.getDetail();
    }
  }, [drawerData]);

  useEffect(() => {
    if (shareId) {
      methods.getShareContent();
    } else {
      V2Message.warning('当前分享链接已失效');
    }
  }, [shareId]);

  // 是否是商场类型
  const isMallType = useMemo(() => {
    const { resourceMallFlag } = detail || {};
    return !!resourceMallFlag;
  }, [detail]);

  // methods
  const methods = useMethods({
    getShareContent() {
      getShareContent({ id: shareId }).then((res) => {
        const content = JSON.parse(res.content || '{}'); // 在 src/views/spotevaluation/pages/index/components/AreaDetailDrawer/components/Top/index.tsx 中的 getShareId 中的 content 传入
        const { configSwitch: config, globalAliasItemVOs, waterText, isALQ } = content || {};
        // console.log('content', content);
        setShareParams({ globalAliasItemVOs, isALQ }); // 0319isALQ租户特殊处理
        config && Object.keys(config).length && setConfigSwitch(config);
        setDrawerData({
          ...content,
          type: content.type || 1, // 未定义时默认为街边店，兼容历史已经分享的
          open: true,
        });
        setWaterText(waterText);
      });
    },
    getDetail: async () => { // 获取商圈详情
      setLoading(true);
      // const detailParams = {
      //   lng: drawerData.lng,
      //   lat: drawerData.lat,
      //   tenantId: drawerData.tenantId,
      //   poiName: drawerData.poiName,
      //   type: drawerData.type,
      //   modelClusterId: drawerData.modelClusterId,
      //   modelConfigId: drawerData.modelConfigId
      // };
      const {
        lng, lat, poiName, type, modelClusterId, modelConfigId, tenantId,
        poiSearchType, radius, poiSearchMinute, poiSearchDistance, points,
      } = drawerData as any;
      const params: any = {
        type,
        modelConfigId,
        tenantId,
      };
      if (type === SpotTypeEnum.Mall) {
        params.modelClusterId = modelClusterId;
      }
      if (type === SpotTypeEnum.Street) {
        params.lng = lng;
        params.lat = lat;
        params.poiSearchType = poiSearchType;
        params.radius = radius;
        // 时间/距离 tab 二选一透传
        params.poiSearchMinute = poiSearchMinute;
        params.poiSearchDistance = poiSearchDistance;
        params.points = points;
      }
      if (poiName) {
        params.poiName = poiName;
      }
      if (drawerData.id) {
        params.recordId = drawerData.id;
      }
      Promise.all([
        spotEvaluationDetailShare(params),
        spotEvaluationOverviewShare(params),
      ])
        .then(([detailRes, overviewRes]) => {

          /**
           * 以下逻辑同 src/views/spotevaluation/pages/index/components/AreaDetailDrawer/index.tsx
           * 如果有修改，记得同步
           */

          /**
           *
           * 大部分详情接口需要传入以下参数，放这里方便复制
            params.poiSearchType = detail?.poiSearchType;
            params.radius = detail?.radius;
            params.poiSearchMinute = detail?.poiSearchMinute;
            params.points = detail?.points;
           */

          // 接口说不会返回以下参数，所以这里手动赋值
          Object.assign(detailRes, {
            poiSearchType,
            rangeMode: +poiSearchMinute > 0 ? RangeModeEnum.Time : RangeModeEnum.Distance,
            radius,
            poiSearchMinute,
            poiSearchDistance,
          });

          if (poiSearchType !== typeEnum.radius) { // 详情接口没有围栏数据；分享详情接口有围栏数据
            // 优先使用接口的数据
            if (Array.isArray(detailRes.polygon) && detailRes.polygon.length) {
              // 地图绘制的围栏用这个参数，主要是不想再改组件用到的地方
              // detailRes.polygon = detailRes.polygon;
              // 其他接口的围栏入参用这个参数，主要是不想在用到的十几个接口重新转一遍
              detailRes.points = detailRes.polygon.map(item => ({ lng: item[0], lat: item[1] }));
            } else if (Array.isArray(points) && points.length) {
              // 地图绘制的围栏用这个参数，主要是不想再改组件用到的地方
              detailRes.polygon = points.map(item => [item.lng, item.lat]);
              // 其他接口的围栏入参用这个参数，主要是不想在用到的十几个接口重新转一遍
              detailRes.points = points;
            }
          }

          setDetail(detailRes);
          const { modelConfigCode } = overviewRes || {};
          setIsSemirModel(modelConfigCode === SemirModelCode);
          setOverViewData(overviewRes);
        })
        .catch((error) => {
          console.error('Error fetching details:', error);
        })
        .finally(() => {
          setLoading(false);
          setLoaded(true);
        });
    },
    // 当前为选址地图埋点，需替换
    eventTracking: (isOpen = true) => { // 埋点
      if (isOpen) {
        openTimeRef.current = new Date().getTime();
        return;
      }
      // 关闭的时候上报总时长
      const curTimestamp = new Date().getTime();
      const timeLag = curTimestamp - openTimeRef.current; // 停留时长
      if (openTimeRef.current && curTimestamp && timeLag > 0) {
        window.LHBbigdata.send({
          event_id: 'dcf71805-ed9e-a3e9-1c09-85daf0d49e02', // 事件id
          msg: {
            viewTimestamp: timeLag
          }
        });
        openTimeRef.current = 0;
      }
    },
  });

  return (
    <div className={styles.container}>
      {(loading || !isLoaded)
        ? <SkeletonMapDetail/>
        : <V2Container
          // 容器上下padding 0， 所以减去就是0
          style={{ height: '100vh' }}
          extraContent={{
            top: <Top
              detail={detail}
              drawerData={drawerData}
              tenantId={drawerData.tenantId}
              shareId={shareId}
            />,
          }}
        >
          <Main
            // viewChanceDetail={false}
            drawerData={drawerData}
            detail={detail}
            overViewData={overViewData}
            isMallType={isMallType}
            // showChancePoint={false}
            tenantId={drawerData.tenantId}
            configSwitch={configSwitch}
            shareParams={shareParams}
            isSemirModel={isSemirModel}
          />
        </V2Container>
      }
      <V2WaterMark show={!!waterText} content={waterText} />
    </div>
  );
};

export default ShareSpotEvaluation;
